import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Check, ClipboardList, LogIn } from "lucide-react";
import { warmGps } from "@/lib/gps";

// Mobilní spodní lišta varianty B (194): skleněná plovoucí lišta, 2 + 2 odkazy
// a uprostřed vyvýšené tlačítko. Desktop ji nevidí (md:hidden).
// 197: odstup od spodní hrany jako ProfitPilot (nad domovskou čárou iPhonu, ne
// o celou její výšku výš) a kapsle aktivní záložky, která při přepnutí přejede.
const isOn = (to, pathname) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));

export default function MobileBottomBar({ left, right, center, hidden }) {
  const { pathname } = useLocation();
  const slots = left.length + 1 + right.length;
  // nejdelší shoda vyhrává (/ekonomika vs /ekonomika/doklady u účetní)
  const all = [...left.map((it, i) => [it, i]), ...right.map((it, j) => [it, left.length + 1 + j])];
  const hit = all.filter(([it]) => isOn(it.to, pathname)).sort((a, b) => b[0].to.length - a[0].to.length)[0];
  const active = hit ? hit[1] : -1;
  const Tab = ([it, idx]) => {
    const on = idx === active;
    return (
      <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={`${it.testid}-mobile`} aria-current={on ? "page" : undefined}
        className={`relative z-[1] flex-1 min-w-0 flex flex-col items-center justify-center gap-[3px] rounded-[27px] text-[11px] font-bold transition-[color,transform] duration-200 active:scale-[.94] motion-reduce:transition-none ${on ? "text-foreground" : "text-muted-foreground"}`}>
        <it.icon size={22} aria-hidden="true" />
        <span className="max-w-full truncate px-0.5">{it.label}</span>
      </NavLink>
    );
  };
  return (
    <>
      {/* obsah pod lištou mizí do pozadí, ať se nepere s popisky */}
      <div aria-hidden="true" className={`md:hidden fixed bottom-0 inset-x-0 z-[29] pointer-events-none pp-veil transition-opacity duration-200 ${hidden ? "opacity-0" : ""}`} />
    <nav aria-label="Hlavní navigace" data-testid="mobile-bottom-nav"
      className={`md:hidden fixed z-30 left-3 right-3 h-16 rounded-[32px] pp-gw pp-bar transition-transform duration-300 motion-reduce:transition-none ${hidden ? "translate-y-[160%]" : ""}`}>
      <div className="absolute inset-[5px] flex">
        <span aria-hidden="true" className="pp-capsule absolute inset-y-0 left-0 rounded-[27px]"
          style={{ width: `${100 / slots}%`, transform: `translateX(${Math.max(active, 0) * 100}%)`, opacity: active < 0 ? 0 : 1 }} />
        {left.map((it, i) => Tab([it, i]))}
        <div className="relative z-[2] flex-1 min-w-0">{center}</div>
        {right.map((it, j) => Tab([it, left.length + 1 + j]))}
      </div>
    </nav>
    </>
  );
}

const HOLD_MS = 900;
const BG = { idle: "hsl(var(--accent-orange))", working: "hsl(var(--success))", ended: "#17181b", draft: "hsl(var(--accent-orange))" };
const RING = { idle: "hsl(var(--success))", working: "#17181b", ended: "hsl(var(--accent-orange))", draft: "#17181b" };

// Středové tlačítko docházky: příchod / odchod se potvrzuje podržením (kroužek se
// zaplní), aby se nikdo nepřihlásil omylem v kapse. Barvy: terakota = nepřihlášen,
// zelená = v práci (ukazuje čas), černá = dnes hotovo.
// Android: bez kontextového menu a výběru textu při podržení, pointer capture
// toleruje mírný pohyb prstu; klávesnice (Enter/mezerník) spustí akci hned.
export function ShiftHoldButton({ state = "idle", hm = "", label, shortLabel, ariaLabel, onHold, onTap }) {
  const [holding, setHolding] = useState(false);
  const timer = useRef(null);
  const origin = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const cancel = () => { clearTimeout(timer.current); timer.current = null; origin.current = null; setHolding(false); };
  const down = (e) => {
    if (e.button != null && e.button !== 0) return;
    origin.current = { x: e.clientX, y: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* starší WebView */ }
    setHolding(true);
    if (state !== "draft") warmGps();   // 215: poloha se hledá už během podržení (jen když je povolená)
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null; origin.current = null; setHolding(false);
      try { if (navigator.vibrate) navigator.vibrate(30); } catch { /* iOS vibrace nemá */ }
      onHold?.();
    }, HOLD_MS);
  };
  const move = (e) => {
    const o = origin.current;
    if (!timer.current || !o) return;
    if (Math.abs(e.clientX - o.x) > 40 || Math.abs(e.clientY - o.y) > 40) cancel();
  };
  const up = () => { if (!timer.current) return; cancel(); onTap?.(); };

  const s = BG[state] ? state : "idle";
  return (
    <>
      <button type="button" data-testid="mobile-fab" aria-label={ariaLabel || label}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}
        onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => { if (e.detail === 0) onHold?.(); }}
        className={`pp-hold absolute left-1/2 -top-[34px] -ml-8 w-16 h-16 rounded-full grid place-items-center text-white ${holding ? "is-holding" : ""}`}
        style={{ background: BG[s] }}>
        <svg aria-hidden="true" viewBox="0 0 74 74" width="74" height="74" className="absolute -left-[5px] -top-[5px] -rotate-90 pointer-events-none">
          <circle className={`pp-ring ${holding ? "on" : ""}`} cx="37" cy="37" r="34.5" stroke={RING[s]} />
        </svg>
        {s === "working" ? <span className="font-heading text-base font-extrabold tabular-nums">{hm}</span>
          : s === "ended" ? <Check size={26} strokeWidth={2.6} aria-hidden="true" />
          : s === "draft" ? <ClipboardList size={24} aria-hidden="true" />
          : <LogIn size={26} strokeWidth={2.3} aria-hidden="true" />}
      </button>
      {/* úzké telefony (360 px, Samsung A, Xiaomi) — krátký popisek, ať nezasahuje do sousedních záložek */}
      <span aria-hidden="true" className="absolute inset-x-0 bottom-[4px] text-center text-[11px] leading-[13px] font-bold whitespace-nowrap text-foreground pointer-events-none">
        <span className="hidden min-[380px]:inline">{label}</span><span className="min-[380px]:hidden">{shortLabel || label}</span>
      </span>
    </>
  );
}

// Středové tlačítko bez podržení (účetní = doklad, projektant = deník).
export function CenterTapButton({ icon: Icon, label, onClick, testid = "mobile-fab" }) {
  return (
    <>
      <button type="button" data-testid={testid} aria-label={label} onClick={onClick}
        className="pp-hold absolute left-1/2 -top-[34px] -ml-8 w-16 h-16 rounded-full grid place-items-center text-white bg-brand">
        <Icon size={24} aria-hidden="true" />
      </button>
      <span aria-hidden="true" className="absolute inset-x-0 bottom-[4px] text-center text-[11px] leading-[13px] font-bold whitespace-nowrap text-foreground pointer-events-none">{label}</span>
    </>
  );
}
